/**
 * Form validation utility functions for client-side validation
 */

// Email validation regex pattern
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Password validation regex patterns
const PASSWORD_REGEX = {
  minLength: /.{8,}/,
  hasUppercase: /[A-Z]/,
  hasLowercase: /[a-z]/,
  hasNumber: /[0-9]/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
};

/**
 * Validates an email address
 * @param {string} email - The email to validate
 * @returns {boolean} - Whether the email is valid
 */
export const isValidEmail = (email) => {
  return EMAIL_REGEX.test(email);
};

/**
 * Validates a password against security requirements
 * @param {string} password - The password to validate
 * @returns {Object} - Object containing validation results
 */
export const validatePassword = (password) => {
  return {
    isValid: PASSWORD_REGEX.minLength.test(password) &&
            PASSWORD_REGEX.hasUppercase.test(password) &&
            PASSWORD_REGEX.hasLowercase.test(password) &&
            PASSWORD_REGEX.hasNumber.test(password) &&
            PASSWORD_REGEX.hasSpecialChar.test(password),
    requirements: {
      minLength: PASSWORD_REGEX.minLength.test(password),
      hasUppercase: PASSWORD_REGEX.hasUppercase.test(password),
      hasLowercase: PASSWORD_REGEX.hasLowercase.test(password),
      hasNumber: PASSWORD_REGEX.hasNumber.test(password),
      hasSpecialChar: PASSWORD_REGEX.hasSpecialChar.test(password)
    }
  };
};

/**
 * Generates password strength feedback
 * @param {string} password - The password to evaluate
 * @returns {Object} - Object containing strength score and feedback
 */
export const getPasswordStrength = (password) => {
  if (!password) {
    return { score: 0, feedback: 'Password is required' };
  }
  
  const validation = validatePassword(password);
  const requirements = validation.requirements;
  
  // Calculate score based on requirements met (0-5)
  const score = Object.values(requirements).filter(Boolean).length;
  
  let feedback = '';
  
  switch (score) {
    case 0:
    case 1:
      feedback = 'Very weak - Please use a stronger password';
      break;
    case 2:
      feedback = 'Weak - Add more variety to your password';
      break;
    case 3:
      feedback = 'Fair - Getting better, but still not strong enough';
      break;
    case 4:
      feedback = 'Good - Almost there!';
      break;
    case 5:
      feedback = 'Strong - Excellent password!';
      break;
    default:
      feedback = 'Please enter a password';
  }
  
  return { score, feedback };
};

/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The input to sanitize
 * @returns {string} - The sanitized input
 */
export const sanitizeInput = (input) => {
  if (!input) return '';
  
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Validates a name (first name, last name)
 * @param {string} name - The name to validate
 * @returns {boolean} - Whether the name is valid
 */
export const isValidName = (name) => {
  return name && name.trim().length >= 2 && name.trim().length <= 50;
};

/**
 * Validates a phone number
 * @param {string} phone - The phone number to validate
 * @returns {boolean} - Whether the phone number is valid
 */
export const isValidPhone = (phone) => {
  // Basic phone validation - can be enhanced for specific formats
  const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/;
  return phoneRegex.test(phone);
};

/**
 * Form validation rules for react-hook-form
 */
export const validationRules = {
  firstName: {
    required: 'First name is required',
    minLength: {
      value: 2,
      message: 'First name must be at least 2 characters'
    },
    maxLength: {
      value: 50,
      message: 'First name must be less than 50 characters'
    }
  },
  lastName: {
    required: 'Last name is required',
    minLength: {
      value: 2,
      message: 'Last name must be at least 2 characters'
    },
    maxLength: {
      value: 50,
      message: 'Last name must be less than 50 characters'
    }
  },
  email: {
    required: 'Email is required',
    pattern: {
      value: EMAIL_REGEX,
      message: 'Please enter a valid email address'
    }
  },
  password: {
    required: 'Password is required',
    minLength: {
      value: 8,
      message: 'Password must be at least 8 characters'
    },
    validate: {
      hasUppercase: (value) => PASSWORD_REGEX.hasUppercase.test(value) || 'Password must contain at least one uppercase letter',
      hasLowercase: (value) => PASSWORD_REGEX.hasLowercase.test(value) || 'Password must contain at least one lowercase letter',
      hasNumber: (value) => PASSWORD_REGEX.hasNumber.test(value) || 'Password must contain at least one number',
      hasSpecialChar: (value) => PASSWORD_REGEX.hasSpecialChar.test(value) || 'Password must contain at least one special character'
    }
  },
  confirmPassword: {
    required: 'Please confirm your password',
    validate: (value, formValues) => value === formValues.password || 'Passwords do not match'
  },
  phone: {
    pattern: {
      value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
      message: 'Please enter a valid phone number'
    }
  },
  terms: {
    required: 'You must accept the terms and conditions'
  }
};